'use client'

import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react'
import Image from 'next/image'
import { Button } from '@ui/base/button'
import { Field, FieldError, FieldLabel } from '@ui/base/field'
import { Input } from '@ui/base/input'
import { Item, ItemActions, ItemButton, ItemContent, ItemDescription, ItemMedia, ItemTitle } from '@ui/base/item'
import { cn } from '@utils/cn'
import { ImageUpIcon } from 'lucide-react'

const allowedTypes = ['image/jpeg', 'image/png', 'image/webp']
const maxSize = 5 * 1024 * 1024

const coverAccept = allowedTypes.join(',')

export const coverIsAllowed = (file: File) => allowedTypes.includes(file.type) && file.size <= maxSize

const inputId = 'fulfillment-cover'

/** Имя кнопки-зоны: то же, что видно в ней заголовком. */
const pickerTitle = 'Перетащите фото сюда или выберите на диске'

/** Файлы меньше мегабайта показываем в килобайтах: «0 МБ» ни о чём не говорит. */
const sizeLabel = (size: number) =>
  size < 1024 * 1024
    ? `${Math.max(Math.round(size / 1024), 1)} КБ`
    : `${(size / (1024 * 1024)).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} МБ`

type CoverFieldProps = {
  file: File | null
  /** Адрес карточки: по нему показываем фото, загруженное в прошлый раз. */
  slug?: string
  hasExistingCover: boolean
  error: string
  onSelect: (file: File | undefined) => void
}

export const CoverField = ({ file, slug, hasExistingCover, error, onSelect }: CoverFieldProps) => {
  const input = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  // Ссылку на файл готовим сразу при показе: иначе на один кадр остаётся предпросмотр прошлого файла.
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : ''), [file])

  // Браузер держит файл в памяти, пока ссылку не освободили: делаем это при смене файла и при уходе со страницы.
  useEffect(
    () => () => {
      if (preview) {
        URL.revokeObjectURL(preview)
      }
    },
    [preview]
  )

  // Поле очищаем перед открытием: иначе повторный выбор того же файла не даёт события change.
  const openPicker = () => {
    if (input.current) {
      input.current.value = ''
      input.current.click()
    }
  }

  const dropFile = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragging(false)
    onSelect(event.dataTransfer.files[0])
  }

  return (
    <Field data-invalid={Boolean(error)}>
      <FieldLabel htmlFor={inputId}>Главное фото</FieldLabel>
      <Input
        ref={input}
        id={inputId}
        type="file"
        accept={coverAccept}
        className="sr-only"
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${inputId}-error` : undefined}
        onChange={(event) => onSelect(event.target.files?.[0])}
      />
      {file && (
        <Item variant="outline" size="sm">
          <ItemMedia variant="image" className="size-12">
            {preview && <Image src={preview} alt="" width={128} height={128} unoptimized />}
          </ItemMedia>
          <ItemContent className="flex-none">
            <ItemTitle>{file.name}</ItemTitle>
            <ItemDescription>{sizeLabel(file.size)}</ItemDescription>
          </ItemContent>
          <ItemActions>
            <Button type="button" variant="outline" size="sm" onClick={openPicker}>
              Заменить
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => onSelect(undefined)}>
              Убрать
            </Button>
          </ItemActions>
        </Item>
      )}
      {!file && hasExistingCover && (
        <Item variant="outline" size="sm">
          <ItemMedia variant="image" className="size-12">
            {slug && (
              <Image src={`/api/server/fulfillment/${slug}/cover`} alt="" width={128} height={128} unoptimized />
            )}
          </ItemMedia>
          <ItemContent className="flex-none">
            <ItemTitle>Текущее фото</ItemTitle>
            <ItemDescription>Фото уже загружено. Новое фото заменит его.</ItemDescription>
          </ItemContent>
          <ItemActions>
            <Button type="button" variant="outline" size="sm" onClick={openPicker}>
              Заменить
            </Button>
          </ItemActions>
        </Item>
      )}
      {!file && !hasExistingCover && (
        // Зона выбора — настоящая кнопка: до неё нужно доходить с клавиатуры, а не только мышью.
        // Имя кнопки задано отдельно, иначе программа чтения с экрана прочитала бы вместе с ним и
        // требования к файлу — они остаются пояснением, которое звучит после имени.
        <Item
          render={<ItemButton />}
          variant="outline"
          size="sm"
          aria-label={pickerTitle}
          aria-describedby={`${inputId}-requirements`}
          className={cn(
            'w-full cursor-pointer flex-col items-center gap-3 border-dashed py-8 text-center',
            dragging && 'border-primary bg-accent/40'
          )}
          onClick={openPicker}
          onDragOver={(event: DragEvent<HTMLDivElement>) => {
            event.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={dropFile}
        >
          <ItemMedia variant="accent">
            <ImageUpIcon />
          </ItemMedia>
          <ItemContent className="flex-none items-center">
            <ItemTitle>{pickerTitle}</ItemTitle>
            <ItemDescription id={`${inputId}-requirements`}>
              JPG, PNG или WebP, не больше 5 МБ. Это фото видно в каталоге.
            </ItemDescription>
          </ItemContent>
        </Item>
      )}
      <FieldError id={`${inputId}-error`}>{error}</FieldError>
    </Field>
  )
}
